/**
 * Demo-Bewertungstexte für den AssessmentEditor.
 *
 * Wird vom „Beispieltexte einfügen"-Button im Editor verwendet — gibt dem
 * Berater eine vollständige Vorlage, die er anpasst statt von leerer Seite
 * zu starten. Die Texte stammen aus dem Konzeptdokument (Frau Mustermann
 * Beispielfall, gelbe Ampel, BG mit zwei Kindern).
 */

import type { AssessmentDraft } from '../store/wizard';

export function makeDemoAssessmentDraft(): Omit<AssessmentDraft, 'lastEditedAt'> {
  return {
    revenueAssessment: 'stabil über 12 Monate',
    costsAssessment: 'branchenüblich',
    profitAssessment: 'unter Bedarfsgrenze',
    hourlyRateAssessment: 'marktkonform für Branche',
    liquidityAssessment: 'ausreichend für 6 Wochen',
    runwayAssessment: 'unbegrenzt bei aktueller Auftragslage',
    utilizationAssessment: 'ausbaufähig',
    gapInterpretation: '',
    verdictLongParagraph:
      'Die Tragfähigkeit der selbständigen Tätigkeit ist nach Auswertung der vorgelegten Unterlagen und des Beratungsgesprächs eingeschränkt gegeben. Der erwirtschaftete Gewinn deckt aktuell rund 80 % des Bedarfs der Bedarfsgemeinschaft, sodass eine teilweise ergänzende Hilfebedürftigkeit fortbesteht.',
    verdictReasoning:
      'Die Bewertung stützt sich auf eine sechs-dimensionale Analyse von Identität, Geschäftsmodell, Marktposition, Ressourcen, Gesundheit und privatem Umfeld. Identität und fachliche Eignung sind gut gegeben (Score 70). Die Geschäftsmodell-Skalierbarkeit ist eingeschränkt durch die persönliche Leistungserbringung. Die Ressourcenlage ist solide, aber die Auslastung ist mit aktuell rund 38 Stunden pro Monat unter dem branchenüblichen Mittel. Familienverantwortung mit zwei schulpflichtigen Kindern reduziert die Verfügbarkeit für eine Vollzeit-Vermittlung.',
    recommendationDetailed:
      '1. Fortführung der Selbständigkeit unter folgenden Auflagen: Re-Evaluation in 6 Monaten mit aktualisierter BWA. 2. Einreichung eines Liquiditätsplans für die nächsten 12 Monate. 3. Stundensatz-Überprüfung (Empfehlung: Anhebung um 10 % zur Branchen-Untergrenze). 4. Gespräch über Akquise-Strategie zur Auslastungs-Erhöhung.',
    conditionsParagraph:
      'Die Fortführung wird unter der Auflage einer Re-Evaluation nach 6 Monaten empfohlen.',
    reevaluationProposal:
      'Re-Evaluation am 6. November 2026 mit BWA Q2/Q3 2026 und aktualisiertem Auftragsbestand.',
    constraintDiscussion:
      'Familienverantwortung mit zwei schulpflichtigen Kindern (8 und 12 Jahre) begrenzt die zeitliche Verfügbarkeit für eine Vollzeit-Beschäftigung. Eine Vermittlung in eine Vollzeit-Stelle wäre aktuell unzumutbar im Sinne von § 10 Abs. 1 Nr. 3 SGB II.',
    riskIndicators:
      '· Auslastung 38 h/Monat liegt unter dem Branchenmittel von 80–100 h.\n· Stundensatz von 37 € liegt knapp unter der Branchen-Untergrenze von 40 €.\n· Bedarfsgemeinschaft mit hohem KdU (900 €/Monat) erschwert vollständige Bedarfsdeckung.',
    strengthIndicators:
      '· Stabile Auftragslage seit 14 Monaten ohne nennenswerte Schwankungen.\n· Positive Liquiditätsreichweite, kein erkennbares Klumpenrisiko.\n· Klare fachliche Qualifikation und langjährige Branchenerfahrung.\n· Etabliertes Kundennetzwerk.',
    dimensionTexts: {
      identity: {
        short: 'Solide Qualifikation und Branchenerfahrung',
        detail:
          'Frau Mustermann verfügt über eine abgeschlossene Mediations-Ausbildung und 8 Jahre Berufserfahrung in einer Anwaltskanzlei vor der Selbständigkeit. Die fachliche Eignung für das Geschäftsmodell ist durchgehend dokumentiert.',
      },
      activity: {
        short: 'Tragfähiges Geschäftsmodell, begrenzte Skalierbarkeit',
        detail:
          'Das Geschäftsmodell „Mediation und Konfliktberatung für KMU" ist tragfähig und nachgefragt. Die Skalierbarkeit ist durch die persönliche Leistungserbringung begrenzt — Wachstum ist nur über Stundensatz-Erhöhung oder zusätzliche Berater möglich.',
      },
      style: {
        short: 'Klare Marktposition, Pricing am unteren Rand',
        detail:
          'Die Zielgruppe (KMU mit 5 bis 50 Mitarbeitenden) ist scharf umrissen. Die Differenzierung erfolgt über Branchen-Spezialisierung. Der Stundensatz von 37 € liegt knapp unter der Branchen-Untergrenze von 40 €; eine Anhebung wird empfohlen.',
      },
      resources: {
        short: 'Solide Ressourcenlage, niedrige Auslastung',
        detail:
          'Liquide Mittel von 2.100 € decken die Burn-Rate von 980 € für rund 8 Wochen. Bei aktuellem Cashflow ist die Reichweite faktisch unbegrenzt. Die Auslastung von 38 Stunden/Monat ist mit rund 24 % unter dem Branchenmittel und sollte schrittweise erhöht werden.',
      },
      health: {
        short: 'Belastbar, ohne erkennbare Einschränkungen',
        detail:
          'Die Selbsteinschätzung der Belastbarkeit ist gut, ohne erkennbare gesundheitliche Einschränkungen, die die Tragfähigkeit gefährden könnten. Pausen- und Erholungsstrategien sind etabliert.',
      },
      private: {
        short: 'Familienverantwortung als Zumutbarkeitsgrund',
        detail:
          'Bedarfsgemeinschaft mit Partner und zwei schulpflichtigen Kindern (8 und 12 Jahre). Die Kinderbetreuung ist nachmittags durch Hort gesichert, was eine flexible Arbeitszeit ermöglicht — eine Vollzeit-Vermittlung wäre jedoch unzumutbar.',
      },
    },
  };
}
